/**
 * Pure URL canonicalization. Returns the canonical URL string if the input
 * is non-canonical, or `null` if the input is already canonical (I-8).
 *
 * Drives the ClientRouteCanonicalizer client component for ALL routes, and
 * is partially mirrored into vercel.json edge redirects.
 *
 * Edge-mirroring is INTENTIONALLY limited to path-changing redirects: the
 * mode-dual route (actions -> /w/:slug/actions) and the retired
 * /command-center -> /cockpit alias — so each redirect terminates. Same-path ?client= stripping
 * (portfolio-only home|clients|help, and admin/*) is NOT mirrored to the edge:
 * Vercel forwards the query string to the destination, so a same-path redirect
 * (/clients?client=X -> /clients) re-appends ?client=X, re-matches the `has`
 * condition, and loops -> ERR_TOO_MANY_REDIRECTS. The client-side canonicalizer
 * handles those routes after mount (a brief stray ?client= in the address bar
 * is acceptable; it does not break rendering).
 *
 * Spec: docs/superpowers/specs/2026-04-24-top-bar-removal-shell-consolidation-design.md §4
 */

const MODE_DUAL_ROUTES = new Set(["actions"]);
// /cockpit is a cross-client portfolio view (Dave, 2026-09-21): a ?client=
// on it is URL pollution, not scope.
const PORTFOLIO_ONLY_ROUTES = new Set(["home", "clients", "help", "cockpit"]);
// /command-center was renamed to /cockpit. There is no /w/:slug/command-center
// route, so old links — with or without ?client= — go to the portfolio
// cockpit rather than a client-scoped dead end (navigation_audit).
const RETIRED_ALIASES: Record<string, string> = {
  "command-center": "/cockpit",
};
const PORTFOLIO_ONLY_PREFIXES = ["admin/"] as const;

function isPortfolioOnly(pathSegment: string, fullPath: string): boolean {
  if (PORTFOLIO_ONLY_ROUTES.has(pathSegment)) return true;
  return PORTFOLIO_ONLY_PREFIXES.some((p) => fullPath.startsWith(`/${p}`));
}

function buildQueryString(params: URLSearchParams): string {
  const remaining = params.toString();
  return remaining ? `?${remaining}` : "";
}

export function canonicalizeUrl(
  pathname: string,
  searchParams: URLSearchParams,
): string | null {
  // Already-canonical scoped URLs: strip residual ?client= but keep other
  // params. The path slug is the source of truth — any ?client= reaching
  // here (e.g., preserved through Vercel's 307) is URL pollution.
  //
  // This branch is URL hygiene, not crash defense. The original blank-page
  // mechanism (useBlocker mount error against <BrowserRouter>) was killed
  // by PR #54's useNavAwayConfirm rewrite. A 2026-04-26 Phase 0
  // investigation (see spec below) disabled this branch and verified that
  // /w/<slug>/<route>?client=<X> mounts cleanly across 4 route shapes ×
  // matching/mismatching slugs × cold-load and in-app nav. Removing this
  // branch will leave a stray ?client= visible in the address bar but
  // will not break rendering.
  //
  // Pinned by:
  //   src/components/shell2/route-renders-without-canonicalizer.test.tsx
  // Investigation receipt:
  //   docs/superpowers/specs/2026-04-25-residual-client-param-route-crash-design.md
  if (pathname.startsWith("/w/")) {
    if (!searchParams.has("client")) return null;
    const remaining = new URLSearchParams(searchParams);
    remaining.delete("client");
    return `${pathname}${buildQueryString(remaining)}`;
  }

  const firstSegment = pathname.replace(/^\//, "").split("/")[0] ?? "";
  const clientSlug = searchParams.get("client");

  const alias = RETIRED_ALIASES[firstSegment];
  if (alias && pathname === `/${firstSegment}`) {
    const remaining = new URLSearchParams(searchParams);
    remaining.delete("client");
    return `${alias}${buildQueryString(remaining)}`;
  }

  // No ?client= param → nothing to normalize
  if (!clientSlug) return null;

  // Mode-dual: ?client=X → /w/X/<route>
  if (MODE_DUAL_ROUTES.has(firstSegment)) {
    const remaining = new URLSearchParams(searchParams);
    remaining.delete("client");
    return `/w/${clientSlug}/${firstSegment}${buildQueryString(remaining)}`;
  }

  // Portfolio-only: strip ?client= but keep other params and same path
  if (isPortfolioOnly(firstSegment, pathname)) {
    const remaining = new URLSearchParams(searchParams);
    remaining.delete("client");
    return `${pathname}${buildQueryString(remaining)}`;
  }

  // Unknown route — leave alone
  return null;
}
